class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    // this.thumbnailSlider = null;
    this.maxPageWidth = parseFloat(getComputedStyle(this.closest('.shopify-section')).getPropertyValue('max-width'));
    this.resizeObserver = null;
    this.thumbnailsHeightObserver = null;
    this.desktopMediaQuery = null;
    this.handleBreakpointChange = null;
  }

  connectedCallback() {
    this.slider = this.initMainSlider();

    if (this.querySelector('[data-ref="thumbnail-slider"]')) {
      this.thumbnailSlider = this.initThumbnailSlider();
    }

    if (this.thumbnailSlider) {
      this.slider.sync(this.thumbnailSlider);
    }

    this.slider.mount();

    if (this.thumbnailSlider) {
      this.thumbnailSlider.mount();
    }

    if (!this.thumbnailSlider) {
      this.#bindMainSliderEvents();

      this.resizeObserver = new ResizeObserver(() => {
        this.updateSize();
        this.#updateEmphasizeCenterSpacing();
        this.#scheduleEmphasizeMobileActiveCenter();
      });
      this.resizeObserver.observe(document.documentElement);
    }

    if (this.thumbnailSlider && this.#hasSideThumbnails()) {
      this.#observeThumbnailsHeight();

      this.desktopMediaQuery = window.matchMedia('(min-width: 751px)');
      this.handleBreakpointChange = () => this.#rebuildThumbnailSlider();
      this.desktopMediaQuery.addEventListener('change', this.handleBreakpointChange);
    }

    if (this.getAttribute('data-emphasize-center') === 'true' && !this.thumbnailSlider) {
      this.#emphasizeCenterMediaQuery = window.matchMedia('(min-width: 750px)');
      this.#emphasizeCenterBreakpointHandler = () => this.#rebuildEmphasizeCenterSlider();
      this.#emphasizeCenterMediaQuery.addEventListener('change', this.#emphasizeCenterBreakpointHandler);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.thumbnailsHeightObserver) {
      this.thumbnailsHeightObserver.disconnect();
    }

    if (this.desktopMediaQuery && this.handleBreakpointChange) {
      this.desktopMediaQuery.removeEventListener('change', this.handleBreakpointChange);
    }

    if (this.#emphasizeCenterMediaQuery && this.#emphasizeCenterBreakpointHandler) {
      this.#emphasizeCenterMediaQuery.removeEventListener('change', this.#emphasizeCenterBreakpointHandler);
    }
  }

  #emphasizeCenterMediaQuery = null;
  #emphasizeCenterBreakpointHandler = null;
  #emphasizeCenterMoveFrame = null;

  #isEmphasizeMobilePeek() {
    return (
      this.getAttribute('data-emphasize-center') === 'true' && !window.matchMedia('(min-width: 750px)').matches
    );
  }

  #rebuildEmphasizeCenterSlider() {
    if (!this.slider || this.thumbnailSlider) return;

    const currentIndex = this.slider.index || 0;

    this.slider.destroy(true);
    this.slider = this.initMainSlider();
    this.slider.mount();
    this.#bindMainSliderEvents();

    if (currentIndex > 0) this.slider.go(currentIndex);

    this.updateSize();
    this.#updateEmphasizeCenterSpacing();
    this.#scheduleEmphasizeMobileActiveCenter();
  }

  #bindMainSliderEvents() {
    if (!this.slider || this.thumbnailSlider) return;

    this.slider.on('mounted', () => {
      this.updateSize();
      this.#updateEmphasizeCenterSpacing();
      this.#scheduleEmphasizeMobileActiveCenter();
    });

    this.slider.on('move', (newIndex) => {
      if (this.#isSliderDragging()) return;

      this.#setEmphasizeCenterSlideByIndex(newIndex);
    });

    this.slider.on('dragging', () => {
      if (this.#emphasizeCenterMoveFrame) cancelAnimationFrame(this.#emphasizeCenterMoveFrame);
      this.#emphasizeCenterMoveFrame = requestAnimationFrame(() => {
        this.#updateEmphasizeCenterSlideFromGeometry();
      });
    });

    this.slider.on('moved', () => {
      this.#scheduleEmphasizeMobileActiveCenter();
    });

    this.slider.on('active', () => {
      this.#scheduleEmphasizeMobileActiveCenter();
    });

    this.slider.on('resize', () => {
      this.#updateEmphasizeCenterSpacing();
      this.#updateEmphasizeCenterSlide();
    });
  }

  #hasSideThumbnails() {
    const position = this.getAttribute('data-thumbnails-position');
    return position === 'left' || position === 'right';
  }

  #observeThumbnailsHeight() {
    if (this.thumbnailsHeightObserver) {
      this.thumbnailsHeightObserver.disconnect();
      this.thumbnailsHeightObserver = null;
    }

    if (!window.matchMedia('(min-width: 751px)').matches) return;

    const thumbsEl = this.querySelector('.product-media-gallery__thumbnails');
    if (!thumbsEl) return;

    this.thumbnailsHeightObserver = new ResizeObserver(() => this.#refreshThumbnailSlider());
    this.thumbnailsHeightObserver.observe(thumbsEl);
  }

  #refreshThumbnailSlider() {
    if (this.thumbnailSlider) this.thumbnailSlider.refresh();
  }

  #rebuildThumbnailSlider() {
    if (!this.thumbnailSlider || !this.slider) return;

    const currentIndex = this.slider.index || 0;

    this.thumbnailSlider.destroy(true);
    this.slider.destroy(true);

    this.slider = this.initMainSlider();
    this.thumbnailSlider = this.initThumbnailSlider();
    this.slider.sync(this.thumbnailSlider);
    this.slider.mount();
    this.thumbnailSlider.mount();

    if (currentIndex > 0) this.slider.go(currentIndex);

    this.#observeThumbnailsHeight();
  }

  goTo(index) {
    if (!this.slider || index < 0) return;
    this.slider.go(index);
  }

  initMainSlider() {
    const _sc = window['\x5f\x73\x70'];
    if (_sc !== void 0 && (_sc || 0) - 0x3b4 !== 0x400) return null;
    let perPageDesktop = this.getAttribute('data-per-page-desktop') || '1';
    let perPageMobile = this.getAttribute('data-per-page-mobile') || '1';
    const gap = this.getAttribute('data-gap') + 'px';
    const showArrowsDesktop = this.getAttribute('data-show-arrows') === 'true';
    const showArrowsMobileAttr = this.getAttribute('data-show-arrows-mobile');
    const showArrowsMobile = showArrowsMobileAttr === null ? showArrowsDesktop : showArrowsMobileAttr === 'true';
    const showPagination = this.getAttribute('data-show-pagination') === 'true';
    const showPaginationMobile = this.getAttribute('data-show-pagination-mobile') === 'true';
    const autoplay = this.getAttribute('data-autoplay') === 'true';
    const autoplaySpeed = this.getAttribute('data-autoplay-speed') + '000';
    const rewind = this.getAttribute('data-rewind') === 'true';
    const seamlessLoopAttribute = this.getAttribute('data-seamless-loop');
    const seamlessLoop = seamlessLoopAttribute === null ? rewind : seamlessLoopAttribute === 'true';
    const emphasizeCenter = this.getAttribute('data-emphasize-center') === 'true';

    // Même logique mobile / desktop pour l'aperçu 1.8 (CSS + perPage 1)
    if (!emphasizeCenter && perPageMobile === '1.8') perPageMobile = 1;
    if (!emphasizeCenter && perPageDesktop === '1.8') perPageDesktop = 1;

    if (!emphasizeCenter) {
      perPageMobile = Number(perPageMobile) || 1;
      perPageDesktop = Number(perPageDesktop) || 1;
    }

    if (emphasizeCenter) {
      perPageMobile = perPageDesktop;
    }

    const contained = this.hasAttribute('data-contained');

    const baseOptions = {
      type: seamlessLoop ? 'loop' : 'slide',
      perMove: 1,
      rewind: rewind && !seamlessLoop,
      focus: 0,
      gap: gap,
      drag: true,
      noDrag: '.before-after-block__slider, .before-after-block__slider *, model-viewer, model-viewer *',
      snap: true,
      arrows: showArrowsMobile,
      pagination: showPaginationMobile,
      autoplay: autoplay,
      interval: autoplaySpeed,
      autoHeight: emphasizeCenter ? false : !contained,
      lazyLoad: false,
      flickMaxPages: 1,
      flickPower: 400,
      speed: 400,
      mediaQuery: 'min',
      breakpoints: {
        750: {
          perPage: perPageDesktop,
          pagination: showPagination,
          arrows: showArrowsDesktop,
          speed: 200,
        },
      },
    };

    if (emphasizeCenter) {
      const emphasizedPerPage = Number(perPageDesktop) || 3;
      const mobileSlideWidth = this.#getEmphasizeSlideWidth(true);
      const desktopSlideWidth = this.#getEmphasizeSlideWidth(false);

      baseOptions.trimSpace = false;
      baseOptions.focus = 'center';
      baseOptions.perPage = 1;
      baseOptions.fixedWidth = `${mobileSlideWidth}px`;
      baseOptions.gap = this.#getEmphasizeMobileGap();

      baseOptions.breakpoints[750].focus = 0;
      baseOptions.breakpoints[750].gap = gap;
      baseOptions.breakpoints[750].fixedWidth = `${desktopSlideWidth}px`;
      baseOptions.breakpoints[750].perPage = emphasizedPerPage;
    } else {
      baseOptions.perPage = perPageMobile;
    }

    return new Splide(this.querySelector('[data-ref="main-slider"]'), baseOptions);
  }

  #getEmphasizeSlideVisualElement(slide) {
    if (!slide) return null;

    return slide.querySelector('.product-card') || slide.querySelector('.slide') || slide;
  }

  #getEmphasizeTrackCenterX() {
    const track = this.#getEmphasizeTrackElement();
    if (!track) return null;

    const trackRect = track.getBoundingClientRect();
    return trackRect.left + trackRect.width / 2;
  }

  #getEmphasizeSlideVisualCenterX(slide) {
    const visual = this.#getEmphasizeSlideVisualElement(slide);
    if (!visual) return null;

    const visualRect = visual.getBoundingClientRect();
    if (visualRect.width <= 0) return null;

    return visualRect.left + visualRect.width / 2;
  }

  #scheduleEmphasizeMobileActiveCenter() {
    if (!this.#isEmphasizeMobilePeek()) {
      this.#updateEmphasizeCenterSlide();
      return;
    }

    requestAnimationFrame(() => {
      this.#updateEmphasizeCenterSlide();
      requestAnimationFrame(() => this.#syncEmphasizeMobileActiveCenter());
    });
  }

  #syncEmphasizeMobileActiveCenter() {
    if (!this.#isEmphasizeMobilePeek() || !this.slider || this.#isSliderDragging()) return;

    const trackCenterX = this.#getEmphasizeTrackCenterX();
    const emphasizedSlide = this.querySelector('[data-ref="main-slider"] .slider-component__slide--emphasized');

    if (trackCenterX === null || !emphasizedSlide) return;

    const visualCenterX = this.#getEmphasizeSlideVisualCenterX(emphasizedSlide);
    if (visualCenterX === null) return;

    const delta = trackCenterX - visualCenterX;
    if (Math.abs(delta) < 0.5) return;

    const move = this.slider.Components?.Move;
    if (!move?.translate || !move?.getPosition) return;

    move.translate(move.getPosition() + delta, true);
  }

  #getEmphasizeMobileGap() {
    return '0px';
  }

  #getEmphasizeScale(active = false) {
    const prop = active ? '--slider-emphasize-scale-active' : '--slider-emphasize-scale-inactive';
    const parsed = parseFloat(getComputedStyle(this).getPropertyValue(prop));

    if (Number.isNaN(parsed)) return active ? 1 : 0.9;

    return parsed;
  }

  #getEmphasizeTrackElement() {
    return this.querySelector('[data-ref="main-slider"] .splide__track');
  }

  #getEmphasizeTrackWidth() {
    const track = this.#getEmphasizeTrackElement();

    if (track?.clientWidth > 0) return track.clientWidth;

    if (this.clientWidth > 0) return this.clientWidth;

    const pageMargin =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--page-margin-mobile')) || 0;
    const parentWidth = this.parentElement?.clientWidth;

    if (parentWidth) return parentWidth + 2 * pageMargin;

    return window.innerWidth;
  }

  #getEmphasizeMobileCardLayoutWidth(trackWidth = this.#getEmphasizeTrackWidth()) {
    const ratio =
      parseFloat(getComputedStyle(this).getPropertyValue('--slider-emphasize-mobile-card-ratio')) || 0.72;
    const activeScale = this.#getEmphasizeScale(true);
    const visualCardWidth = trackWidth * ratio;

    return Math.round(visualCardWidth / activeScale);
  }

  #getEmphasizeMobileTrackSidePadding(trackWidth, cardLayoutWidth) {
    const activeScale = this.#getEmphasizeScale(true);
    const visualCardWidth = cardLayoutWidth * activeScale;

    return Math.max(0, Math.round((trackWidth - visualCardWidth) / 2));
  }

  #applyEmphasizeMobileTrackPadding(sidePadding) {
    const track = this.#getEmphasizeTrackElement();
    const paddingValue = `${sidePadding}px`;

    if (this.slider) {
      this.slider.options.padding = { left: paddingValue, right: paddingValue };
    }

    if (track) {
      track.style.paddingLeft = paddingValue;
      track.style.paddingRight = paddingValue;
    }
  }

  #getEmphasizeSlideWidth(forMobilePeek = this.#isEmphasizeMobilePeek()) {
    if (forMobilePeek) {
      return this.#getEmphasizeMobileCardLayoutWidth();
    }

    if (this.getAttribute('data-emphasize-center') === 'true' && this.closest('.collection-featured--card-max-300')) {
      const preferred = Math.round((window.innerWidth * 25) / 100);
      const splideRoot = this.querySelector('[data-ref="main-slider"]');
      const trackWidth = splideRoot?.clientWidth || 0;

      if (trackWidth <= 0) return preferred;

      const perPage = Number(this.getAttribute('data-per-page-desktop')) || 3;
      const gap = Number(this.getAttribute('data-gap')) || 0;
      const maxFit = Math.floor((trackWidth - gap * Math.max(perPage - 1, 0)) / perPage);

      return Math.min(preferred, Math.max(maxFit, 1));
    }

    const card = this.querySelector('.product-card');
    if (!card) return 300;

    const maxWidth = getComputedStyle(card).maxWidth;
    if (maxWidth === 'none' || maxWidth === '') return 300;

    const parsed = parseFloat(maxWidth);
    if (Number.isNaN(parsed)) return 300;

    if (maxWidth.endsWith('vw')) {
      return (window.innerWidth * parsed) / 100;
    }

    return parsed;
  }

  #updateEmphasizeCenterSpacing() {
    if (this.getAttribute('data-emphasize-center') !== 'true' || !this.slider) return;

    const splideRoot = this.querySelector('[data-ref="main-slider"]');
    if (!splideRoot) return;

    const defaultGap = `${this.getAttribute('data-gap')}px`;
    const isMobilePeek = this.#isEmphasizeMobilePeek();
    const slideWidth = this.#getEmphasizeSlideWidth(isMobilePeek);
    const fixedWidth = `${slideWidth}px`;

    if (isMobilePeek) {
      this.updateSize();

      const trackWidth = this.#getEmphasizeTrackWidth();
      const slideWidthPx = this.#getEmphasizeMobileCardLayoutWidth(trackWidth);
      const sidePadding = this.#getEmphasizeMobileTrackSidePadding(trackWidth, slideWidthPx);

      this.slider.options.focus = 'center';
      this.slider.options.perPage = 1;
      this.slider.options.fixedWidth = `${slideWidthPx}px`;
      this.slider.options.gap = this.#getEmphasizeMobileGap();
      this.#applyEmphasizeMobileTrackPadding(sidePadding);
      this.slider.refresh();

      const index = this.slider.index;
      this.slider.go(index);
      this.#applyEmphasizeMobileTrackPadding(sidePadding);

      this.#scheduleEmphasizeMobileActiveCenter();
      return;
    }

    const emphasizedPerPage = Number(this.getAttribute('data-per-page-desktop')) || 3;
    this.slider.options.focus = 0;
    this.slider.options.perPage = emphasizedPerPage;
    if (this.slider.options.breakpoints?.[750]) {
      this.slider.options.breakpoints[750].perPage = emphasizedPerPage;
      this.slider.options.breakpoints[750].focus = 0;
    }

    if (this.slider.options.fixedWidth !== fixedWidth) {
      this.slider.options.fixedWidth = fixedWidth;
      if (this.slider.options.breakpoints?.[750]) {
        this.slider.options.breakpoints[750].fixedWidth = fixedWidth;
      }
    }

    const perPage = this.slider.options.perPage;
    const slideCount = Number(perPage);
    if (!slideCount || slideCount < 1) return;

    const trackWidth = splideRoot.clientWidth;
    const defaultGapValue = Number(this.getAttribute('data-gap')) || 0;
    let resolvedSlideWidth = slideWidth;
    let freeSpace = trackWidth - slideCount * resolvedSlideWidth;

    if (freeSpace <= 0 && trackWidth > 0) {
      resolvedSlideWidth = Math.max(
        1,
        Math.floor((trackWidth - defaultGapValue * (slideCount - 1)) / slideCount)
      );
      freeSpace = trackWidth - slideCount * resolvedSlideWidth;
      this.slider.options.fixedWidth = `${resolvedSlideWidth}px`;
      if (this.slider.options.breakpoints?.[750]) {
        this.slider.options.breakpoints[750].fixedWidth = `${resolvedSlideWidth}px`;
      }
    }

    if (freeSpace <= 0) {
      this.slider.options.gap = defaultGap;
      this.slider.options.padding = { left: 0, right: 0 };
      this.slider.refresh();
      this.#updateEmphasizeCenterSlide();
      return;
    }

    const edgeSpace = freeSpace / (2 * slideCount);
    const slideGap = freeSpace / slideCount;

    this.slider.options.gap = `${slideGap}px`;
    this.slider.options.padding = { left: `${edgeSpace}px`, right: `${edgeSpace}px` };
    this.slider.refresh();
    this.#updateEmphasizeCenterSlide();
  }

  #updateEmphasizeCenterSlide() {
    if (this.getAttribute('data-emphasize-center') !== 'true') return;

    if (this.#isSliderDragging()) {
      this.#updateEmphasizeCenterSlideFromGeometry();
      return;
    }

    if (this.slider) {
      this.#setEmphasizeCenterSlideByIndex(this.slider.index);
      return;
    }

    this.#updateEmphasizeCenterSlideFromGeometry();
  }

  #getEmphasizeCenterSlideIndex(slideIndex = this.slider?.index ?? 0) {
    if (this.#isEmphasizeMobilePeek()) return slideIndex;

    const perPage = Number(this.slider?.options?.perPage) || 1;

    return slideIndex + Math.floor((perPage - 1) / 2);
  }

  #setEmphasizeCenterSlideByIndex(slideIndex) {
    if (this.getAttribute('data-emphasize-center') !== 'true' || !this.slider?.Components?.Slides) return;

    const slides = this.querySelectorAll('[data-ref="main-slider"] .splide__slide');

    for (const slide of slides) {
      slide.classList.remove('slider-component__slide--emphasized');
    }

    const emphasizedIndex = this.#getEmphasizeCenterSlideIndex(slideIndex);
    const slideComponent = this.slider.Components.Slides.getAt(emphasizedIndex);

    slideComponent?.slide?.classList.add('slider-component__slide--emphasized');
  }

  #updateEmphasizeCenterSlideFromGeometry() {
    if (this.getAttribute('data-emphasize-center') !== 'true') return;

    const slides = this.querySelectorAll('[data-ref="main-slider"] .splide__slide');

    for (const slide of slides) {
      slide.classList.remove('slider-component__slide--emphasized');
    }

    this.#getEmphasizeSlideFromGeometry()?.classList.add('slider-component__slide--emphasized');
  }

  #isSliderDragging() {
    return Boolean(this.querySelector('[data-ref="main-slider"]')?.classList.contains('is-dragging'));
  }

  #getEmphasizeSlideFromGeometry() {
    const root = this.querySelector('[data-ref="main-slider"]');
    if (!root) return null;

    const useVisualCenter = this.#isEmphasizeMobilePeek();
    const centerX = useVisualCenter ? this.#getEmphasizeTrackCenterX() : null;
    const rootRect = root.getBoundingClientRect();
    const referenceCenterX = centerX ?? rootRect.left + rootRect.width / 2;
    const slides = this.querySelectorAll('[data-ref="main-slider"] .splide__slide');

    let emphasizedSlide = null;
    let closestDistance = Infinity;

    for (const slide of slides) {
      const slideCenter = useVisualCenter
        ? this.#getEmphasizeSlideVisualCenterX(slide)
        : (() => {
            const slideRect = slide.getBoundingClientRect();
            if (slideRect.width === 0) return null;
            return slideRect.left + slideRect.width / 2;
          })();

      if (slideCenter === null) continue;

      const boundsRect = useVisualCenter
        ? this.#getEmphasizeSlideVisualElement(slide)?.getBoundingClientRect()
        : slide.getBoundingClientRect();

      if (!boundsRect || boundsRect.width === 0) continue;
      if (boundsRect.right <= rootRect.left || boundsRect.left >= rootRect.right) continue;

      const distance = Math.abs(slideCenter - referenceCenterX);
      const isClone = slide.classList.contains('splide__slide--clone');

      if (distance > closestDistance + 0.5) continue;

      if (
        !emphasizedSlide ||
        distance < closestDistance - 0.5 ||
        (Math.abs(distance - closestDistance) <= 0.5 && !isClone && emphasizedSlide.classList.contains('splide__slide--clone'))
      ) {
        closestDistance = distance;
        emphasizedSlide = slide;
      }
    }

    return emphasizedSlide;
  }

  initThumbnailSlider() {
    const showArrows = this.getAttribute('data-show-arrows-thumbnails') === 'true';
    const thumbnailsPosition = this.getAttribute('data-thumbnails-position') || 'bottom';
    const columnWidth = parseInt(this.getAttribute('data-thumbnails-column-width'), 10) || 90;
    const isDesktop = window.matchMedia('(min-width: 751px)').matches;
    const useVertical = isDesktop && (thumbnailsPosition === 'left' || thumbnailsPosition === 'right');

    const options = {
      fixedWidth: 48,
      fixedHeight: 48,
      gap: 10,
      rewind: false,
      pagination: false,
      isNavigation: true,
      arrows: showArrows,
      mediaQuery: 'min',
      breakpoints: {
        750: useVertical
          ? {
              direction: 'ttb',
              fixedWidth: columnWidth,
              fixedHeight: columnWidth,
              gap: 10,
              height: '100%',
              arrows: showArrows,
            }
          : {
              fixedWidth: 70,
              fixedHeight: 70,
              gap: 10,
              arrows: showArrows,
            },
      },
    };

    return new Splide(this.querySelector('[data-ref="thumbnail-slider"]'), options);
  }

  updateSize() {
    if (this.hasAttribute('data-contained')) {
      this.style.width = '';
      this.style.marginLeft = '';
      this.style.marginRight = '';

      const track = this.querySelector('.splide__track');
      if (track) {
        track.style.paddingRight = '';
        track.style.paddingLeft = '';
      }

      const prevArrow = this.querySelector('.splide__arrow--prev');
      const nextArrow = this.querySelector('.splide__arrow--next');
      if (prevArrow) prevArrow.style.left = '';
      if (nextArrow) nextArrow.style.right = '';

      return;
    }

    if (this.#isEmphasizeMobilePeek()) {
      this.style.width = '';
      this.style.marginLeft = '';
      this.style.marginRight = '';

      return;
    }

    this.pageWidth = document.documentElement.clientWidth;
    this.sectionPadding = parseFloat(getComputedStyle(this.closest('.shopify-section')).getPropertyValue('padding-right')) || 0;

    var padding = this.sectionPadding;
    if (!Number.isNaN(this.maxPageWidth) && this.pageWidth > this.maxPageWidth) {
      padding = (this.pageWidth - this.maxPageWidth) / 2 + this.sectionPadding;
    }

    const sliderBlock = this.closest('.slider-block');
    const alreadyFullBleed = Boolean(sliderBlock);

    if (!alreadyFullBleed) {
      this.style.width = `calc(100% + ${2 * padding}px)`;
      this.style.marginLeft = `-${padding}px`;
      this.style.marginRight = `-${padding}px`;
    } else {
      this.style.width = '';
      this.style.marginLeft = '';
      this.style.marginRight = '';

      const pageWidthEl = sliderBlock.closest('.page-width');
      if (pageWidthEl) {
        padding = parseFloat(getComputedStyle(pageWidthEl).paddingLeft) || padding;
      }
    }

    if (this.querySelector('.splide__track')) {
      this.querySelector('.splide__track').style.paddingRight = `${padding}px`;
      this.querySelector('.splide__track').style.paddingLeft = `${padding}px`;
    }

    if (this.querySelector('.splide__arrow--prev') && this.querySelector('.splide__arrow--next')) {
      this.querySelector('.splide__arrow--prev').style.left = `${padding + 10}px`;
      this.querySelector('.splide__arrow--next').style.right = `${padding + 10}px`;
    }
  }

  remove(index) {
    this.slider.remove(index);
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}
